/**
 * Drawing region on a page, and that region's pixels on the page raster.
 * One page has one scale. Every drawing on it uses that scale.
 */

import { assertUnreachable } from './function.helpers';
import { PDF_POINTS_PER_INCH } from './project-file-drawing-raster-scale.helpers';
import type { ExtractedDrawingPageFrame } from '../models/project-file-drawing.model';

export type NormalizedBbox = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type PixelRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Fraction of the detection box added on each side before it is stored. */
export const DRAWING_CROP_PAD = 0.02;

export type PagePlacement = {
  /** Detection box expanded by {@link DRAWING_CROP_PAD}, in page-frame units. */
  rectPt: PixelRect;
  /** That rectangle on the page raster. */
  pixels: PixelRect;
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

/**
 * Convert a 0–1 page box to integer pixels.
 * {@link pad} is a fraction of the box itself (not the page), so two drawings
 * on one sheet do not eat each other. Degenerate boxes return null.
 */
export const pixelRectFromNormalizedBbox = (
  bbox: NormalizedBbox,
  pageWidth: number,
  pageHeight: number,
  pad: number,
): PixelRect | null => {
  if (pageWidth < 1 || pageHeight < 1) return null;
  if (!(bbox.width > 0) || !(bbox.height > 0)) return null;

  const padX = bbox.width * pad;
  const padY = bbox.height * pad;
  const left = clamp(bbox.left - padX, 0, 1);
  const top = clamp(bbox.top - padY, 0, 1);
  const right = clamp(bbox.left + bbox.width + padX, 0, 1);
  const bottom = clamp(bbox.top + bbox.height + padY, 0, 1);
  const x = Math.floor(left * pageWidth);
  const y = Math.floor(top * pageHeight);
  const width = Math.ceil((right - left) * pageWidth);
  const height = Math.ceil((bottom - top) * pageHeight);
  if (width < 1 || height < 1) return null;
  if (x >= pageWidth || y >= pageHeight) return null;
  return {
    x,
    y,
    width: Math.min(width, pageWidth - x),
    height: Math.min(height, pageHeight - y),
  };
};

/** Width and height of {@link frame} in its own units (points or source pixels). */
export const pageFrameSize = (
  frame: ExtractedDrawingPageFrame,
): { width: number; height: number } => {
  switch (frame.kind) {
    case 'pdf':
      return { width: frame.widthPt, height: frame.heightPt };
    case 'image':
      return { width: frame.widthPx, height: frame.heightPx };
    default:
      return assertUnreachable(frame);
  }
};

/**
 * Pixels per paper inch of a page raster. Null for an image upload.
 * Depends only on the page, so every drawing on that page gets the same value.
 */
export const pageRasterDpi = (
  frame: ExtractedDrawingPageFrame,
  rasterWidthPx: number,
): number | null => {
  switch (frame.kind) {
    case 'image':
      return null;
    case 'pdf': {
      if (!(frame.widthPt > 0) || !(rasterWidthPx > 0)) return null;
      return (rasterWidthPx / frame.widthPt) * PDF_POINTS_PER_INCH;
    }
    default:
      return assertUnreachable(frame);
  }
};

/**
 * Map a page-frame rectangle onto a page raster.
 * Uses the raster's pixel size over the frame, so both axes share that page's scale.
 * The result is clipped to the bitmap; area outside the page is not drawable.
 */
export const pageRectToRasterPixels = (
  rect: PixelRect,
  frame: { width: number; height: number },
  raster: { widthPx: number; heightPx: number },
): PixelRect | null => {
  if (!(frame.width > 0) || !(frame.height > 0)) return null;
  if (!(rect.width > 0) || !(rect.height > 0)) return null;
  if (!(raster.widthPx > 0) || !(raster.heightPx > 0)) return null;
  const x = Math.round((rect.x / frame.width) * raster.widthPx);
  const y = Math.round((rect.y / frame.height) * raster.heightPx);
  const right = Math.round(
    ((rect.x + rect.width) / frame.width) * raster.widthPx,
  );
  const bottom = Math.round(
    ((rect.y + rect.height) / frame.height) * raster.heightPx,
  );
  const x0 = Math.max(0, Math.min(raster.widthPx, x));
  const y0 = Math.max(0, Math.min(raster.heightPx, y));
  const x1 = Math.max(0, Math.min(raster.widthPx, right));
  const y1 = Math.max(0, Math.min(raster.heightPx, bottom));
  const width = x1 - x0;
  const height = y1 - y0;
  if (width < 1 || height < 1) return null;
  return { x: x0, y: y0, width, height };
};

/**
 * Stored region for one detection box on one page raster.
 * {@link pad} is a fraction of the box. The pixel size uses the page raster, not a new render.
 */
export const placeDrawingOnPage = (
  bbox: NormalizedBbox,
  frame: { width: number; height: number },
  raster: { widthPx: number; heightPx: number },
  pad: number,
): PagePlacement | null => {
  const rectPt = pixelRectFromNormalizedBbox(
    bbox,
    frame.width,
    frame.height,
    pad,
  );
  if (!rectPt) return null;
  const pixels = pageRectToRasterPixels(rectPt, frame, raster);
  if (!pixels) return null;
  return { rectPt, pixels };
};
